/* eslint-disable import/named */
/* eslint-disable camelcase */
import { pipe, path, toString, curry, map, evolve, isEmpty } from 'helpers/ramda';
import i18n from 'i18n/config';
import { IBook, IPagination, Fills, LastTradePriceMovement } from 'types/ITrade';
import type { OrderBook } from 'types/publicSocket';
import type { TradeMoment } from 'types/RecentTrades';

import { getInt } from '../../helpers/utils';

const pathToString = curry((_path, obj) => pipe(path(_path), toString)(obj));

const productIdByPayload = pathToString(['payload', 'product_id']);
const productIdByResult = pathToString(['result', 'product_id']);

const mapLimitPricetoPrice = ([price, size, depth]: OrderBook[number]): IBook => ({
  price: String(price),
  size: Number(size),
  depth: String(depth),
  limit_price: String(price),
});

function calcPages(obj: IPagination): number {
  return Math.ceil(obj['x-pagination-total'] / obj['x-pagination-page-size']);
}

const convertPriceToFixed = precision =>
  map(
    evolve({
      price: (_price: number | string) => {
        return parseFloat(_price.toString()).toFixed(precision);
      },
    })
  );

function calcPrecision(data: number | string): number {
  const [, afterDecimal] = data.toString().split('.');
  return afterDecimal ? afterDecimal.length : 0;
}

/**
 * Calculates the price movement direction and other details for a trade.
 *
 * @param trade - The current trade object.
 * @param lastTrade - The previous trade object to compare against.
 * @returns An object with price movement information.
 */
function calcPriceMovement(
  trade: Fills,
  lastTrade?: TradeMoment
): Pick<
  TradeMoment,
  'price' | 'size' | 'buyer_role' | 'timestamp' | 'price_movement' | 'has_changed'
> {
  let direction = 'up';
  const currentPrice = parseFloat(trade.p.toString());
  const lastPrice = lastTrade ? parseFloat(lastTrade.price.toString()) : 0;

  if (currentPrice < lastPrice) {
    direction = 'down';
  } else if (currentPrice > lastPrice) {
    direction = 'up';
  } else {
    // Keep the same direction if price hasn't changed
    direction = lastTrade?.price_movement || 'up';
  }

  return {
    price: trade.p,
    size: trade.s,
    buyer_role: (trade.r === 't' ? 'taker' : 'maker') as 'maker' | 'taker',
    timestamp: trade.t,
    price_movement: direction as LastTradePriceMovement,
    has_changed: currentPrice !== lastPrice,
  };
}

/**
 * Processes an array of recent trades to calculate price movement information for each trade,
 * based on the previous last trade data.
 *
 * @param recent_trades - Array of trade fill objects to process.
 * @param prevlastTrade - The previous last trade object, used as a reference for the first calculation.
 * @returns An array of objects representing price movement information for each trade.
 *
 * @example
 * // Example usage:
 * const trades = [
 *   { p: 100, q: 1, t: 1715172000000 }, // p: price, q: quantity, t: timestamp (microseconds)
 *   { p: 102, q: 2, t: 1715172060000 },
 * ];
 * const prevLast = { price: 99, side: 'buy', datetime: 1715171940000 };
 * const result = populatePriceMovement(trades, prevLast);
 * // result: [
 * //   { price: 100, movement: 'up', side: 'buy', datetime: 1715172000 },
 * //   { price: 102, movement: 'up', side: 'buy', datetime: 1715172060 }
 * // ]
 */
function populatePriceMovement(
  recent_trades: Fills[],
  previousLastTrade: TradeMoment | undefined
): TradeMoment[] {
  if (!recent_trades?.length) return [];

  return recent_trades
    .reduceRight((acc: TradeMoment[], trade) => {
      // either take lastTrade of latest update or previous lastTrade
      const lastTrade = isEmpty(previousLastTrade)
        ? acc[acc.length - 1]
        : previousLastTrade;
      return [
        ...acc,
        {
          ...calcPriceMovement(trade, lastTrade),
          datetime: new Date(trade.t / 1000), // Convert microseconds to milliseconds and create Date object
        } as TradeMoment,
      ];
    }, [] as TradeMoment[])
    .reverse();
}

const extractPaginationHeaders = (headers: IPagination) => ({
  currentPageNum: getInt(headers['x-pagination-page-num']),
  pageSize: getInt(headers['x-pagination-page-size']),
  totalPages: getInt(headers['x-pagination-total']),
});

const processWithdrawalState = withdrawal => {
  switch (withdrawal.state) {
    case 'unconfirmed':
      return {
        api_state: 'unconfirmed',
        state: i18n.t('trading:processWithdrawalState.unconfirmedState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.unconfirmedHelper'),
      };
    case 'confirmed':
      return {
        api_state: 'confirmed',
        state: i18n.t('trading:processWithdrawalState.awaitingProcessing'),
      };
    case 'cancelled':
      return {
        api_state: 'cancelled',
        state: i18n.t('trading:processWithdrawalState.cancelledState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.cancelledHelper'),
      };
    case 'initiated':
      return {
        api_state: 'initiated',
        state: i18n.t('trading:processWithdrawalState.initiatedState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.initiatedHelper'),
      };
    case 'deemed':
      return {
        api_state: 'deemed',
        state: i18n.t('trading:processWithdrawalState.initiatedState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.deemedHelper'),
      };
    case 'complete':
      return {
        api_state: 'complete',
        state: i18n.t('trading:processWithdrawalState.completeState'),
        state_helper_text: i18n.t('trading:processWithdrawalState.completeHelper'),
      };
    default:
      return {
        api_state: '',
        state: '',
        state_helper_text: '',
      };
  }
};

const filterDataByContractType = (contractType: string, data: any[]) => {
  return data.filter(item => item.product.contract_type.includes(contractType));
};

const getLabelByContractType = (contractType: string, label: string) => {
  if (contractType === 'interest_rate_swaps') {
    return label.replace(i18n.t('trading:price'), i18n.t('trading:rate'));
  }
  return label;
};

const PRIVATE_SOCKET_ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

export {
  pathToString,
  productIdByPayload,
  productIdByResult,
  mapLimitPricetoPrice,
  calcPages,
  convertPriceToFixed,
  calcPrecision,
  calcPriceMovement,
  populatePriceMovement,
  extractPaginationHeaders,
  processWithdrawalState,
  filterDataByContractType,
  getLabelByContractType,
  PRIVATE_SOCKET_ACTIONS,
};
